<template>
  <div
    v-if="shareConfig.show && shareConfig.questionData"
    class="share-backdrop"
    @click="handleBackdropClick">
    <div class="share-content">
      <div ref="swiperRef" class="poster-swiper" @scroll.passive="handleSwiperScroll">
        <div
          class="poster-slide"
          :class="{ active: selectedIndex === 0 }"
          @click.stop="scrollToIndex(0)">
          <canvas ref="frontCanvasRef" class="share-canvas"></canvas>
        </div>
        <div
          class="poster-slide"
          :class="{ active: selectedIndex === 1 }"
          @click.stop="scrollToIndex(1)">
          <canvas ref="backCanvasRef" class="share-canvas"></canvas>
        </div>
        <div
          class="poster-slide"
          :class="{ active: selectedIndex === 2 }"
          @click.stop="scrollToIndex(2)">
          <canvas ref="posterCanvasRef" class="share-canvas"></canvas>
        </div>
      </div>
      <div class="poster-dots">
        <button
          v-for="(_, index) in posterLabels"
          :key="index"
          type="button"
          class="dot"
          :class="{ active: selectedIndex === index }"
          @click.stop="scrollToIndex(index)"></button>
      </div>
      <div class="share-buttons-group">
        <!-- <div v-if="isWechat" @click.stop>
          <ShareButton text="分享給好友" theme="light" @click="handleShare" />
        </div> -->
        <div @click.stop>
          <ShareButton text="分享當前海報" theme="dark" @click="handleSaveImg" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useShare } from '@/hooks/useShare';
import { useAuth } from '@/hooks/useAuth';
import { showLoading, hideLoading, getImageUrl } from '@/utils';
import { getReferralInfo } from '@/api/auth';
import { drawCanvas, drawRequestStateCanvas } from './drawCanvas';
import ShareButton from './ShareButton.vue';

const { shareConfig, hideShare } = useShare();
const { isLogin } = useAuth();
const frontCanvasRef = ref<HTMLCanvasElement | null>(null);
const backCanvasRef = ref<HTMLCanvasElement | null>(null);
const posterCanvasRef = ref<HTMLCanvasElement | null>(null);
const swiperRef = ref<HTMLDivElement | null>(null);
const selectedIndex = ref(0);
const posterLabels = ['紙條正面', '紙條反面', '經典海報'];

const handleBackdropClick = () => {
  hideShare();
};

const getCurrentCanvas = () => {
  if (selectedIndex.value === 0) return frontCanvasRef.value;
  if (selectedIndex.value === 1) return backCanvasRef.value;
  return posterCanvasRef.value;
};

const getCurrentDataUrl = () => {
  const selectedCanvas = getCurrentCanvas();
  if (!selectedCanvas) return '';
  return selectedCanvas.toDataURL('image/png', 1);
};

const handleSaveImg = () => {
  const dataUrl = getCurrentDataUrl();
  if (!dataUrl) return;
  const previewContainer = document.createElement('div');
  previewContainer.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.9);
    z-index: 999999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `;
  const img = document.createElement('img');
  img.src = dataUrl;
  img.style.cssText = `
    max-width: 90%;
    object-fit: contain;
  `;
  const hint = document.createElement('div');
  hint.textContent = '長按圖片可將海報分享給好友或保存到相冊';
  hint.style.cssText = `
    color: #FFEFD8;
    margin-top: 20px;
    font-size: 16px;
  `;
  previewContainer.onclick = () => {
    document.body.removeChild(previewContainer);
  };
  img.onclick = e => {
    e.stopPropagation();
  };
  previewContainer.appendChild(img);
  previewContainer.appendChild(hint);
  document.body.appendChild(previewContainer);
};

const scrollToIndex = (index: number, behavior: 'smooth' | 'auto' = 'smooth') => {
  const container = swiperRef.value;
  if (!container) return;
  const slides = Array.from(container.querySelectorAll('.poster-slide')) as HTMLDivElement[];
  const target = slides[index];
  if (!target) return;
  const left = target.offsetLeft - (container.clientWidth - target.clientWidth) / 2;
  container.scrollTo({ left, behavior });
  selectedIndex.value = index;
};

const handleSwiperScroll = () => {
  const container = swiperRef.value;
  if (!container) return;
  const slides = Array.from(container.querySelectorAll('.poster-slide')) as HTMLDivElement[];
  if (!slides.length) return;
  const viewportCenter = container.scrollLeft + container.clientWidth / 2;
  let minDistance = Number.POSITIVE_INFINITY;
  let nearestIndex = 0;
  slides.forEach((slide, index) => {
    const center = slide.offsetLeft + slide.clientWidth / 2;
    const distance = Math.abs(center - viewportCenter);
    if (distance < minDistance) {
      minDistance = distance;
      nearestIndex = index;
    }
  });
  selectedIndex.value = nearestIndex;
};

const redrawCanvas = async () => {
  if (!shareConfig.value.questionData) return;
  if (!frontCanvasRef.value || !backCanvasRef.value || !posterCanvasRef.value) return;
  showLoading();
  let referralCode = '';
  const username = '您的好友';
  if (isLogin()) {
    try {
      const { referralCode: fetchedReferralCode } = await getReferralInfo();
      referralCode = fetchedReferralCode;
    } catch (error) {
      console.error('獲取用戶信息失敗:', error);
    }
  }
  const bgUrl =
    shareConfig.value.slipBackgroundUrl || getImageUrl('result_appear_52.png', 'sequence_frame');
  await Promise.all([
    drawRequestStateCanvas(
      frontCanvasRef.value,
      shareConfig.value.questionData,
      bgUrl,
      false,
      referralCode
    ),
    drawRequestStateCanvas(
      backCanvasRef.value,
      shareConfig.value.questionData,
      bgUrl,
      true,
      referralCode
    ),
    drawCanvas(posterCanvasRef.value, shareConfig.value.questionData, referralCode, username)
  ]);
  hideLoading();
  await nextTick();
  scrollToIndex(0, 'auto');
};

watch(
  () => shareConfig.value.show,
  async show => {
    if (!show) return;
    await nextTick();
    await redrawCanvas();
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.share-backdrop {
  backdrop-filter: blur(4px);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.35);
  display: flex;
  justify-content: center;
  z-index: 99999;
  mix-blend-mode: normal;
}

.share-content {
  margin-top: 5%;
  width: 100%;
}

.poster-swiper {
  width: 100%;
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 0 11vw;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.poster-swiper::-webkit-scrollbar {
  display: none;
}

.poster-slide {
  flex: 0 0 78vw;
  scroll-snap-align: center;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 239, 216, 0.28);
  opacity: 0.72;
  transform: scale(0.96);
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    border-color 0.2s ease;
}

.poster-slide.active {
  opacity: 1;
  transform: scale(1);
  border-color: rgba(255, 239, 216, 0.6);
}

.share-canvas {
  width: 100%;
  display: block;
}

.poster-dots {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: rgba(255, 239, 216, 0.35);
}

.dot.active {
  background: rgba(255, 239, 216, 0.9);
}

.share-buttons-group {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  margin-top: 18px;
}
</style>
